/**
 * Seats down from each host's desired roster (WI 34807, part 2 of Bug 34685).
 * Part 1 reads the bridge's own rows, so a seat that never comes back after a
 * reboot (the 9/27 Studio power cycle) drops off the wall once its row is
 * auto-retired after 7 days, and a seat that never registered never shows at
 * all. The source of what SHOULD be running is each host's
 * desired-roster-mac-<host>.json, which its Control Center publishes as
 * `desiredSeats` on the host-metrics POST it already sends (mac_prime-ryn's
 * half). The bridge stores that body verbatim and
 * `GET /api/host-metrics/latest` returns it as `hosts[].metric`, so no bridge
 * change is needed. The live `hostMetrics` relay drops unknown fields, which is
 * why this reads the durable latest instead.
 *
 * A desired seat is DOWN unless it is running by one of these:
 * - a fresh context snapshot or CC row (the same `observedIds` part 1 uses), or
 * - a bridge heartbeat inside part 1's 15 min liveness window. One window
 *   fits every kind: the codex and grok ids in the rosters are interactive
 *   TUIs that heartbeat about once a minute, like the claude seats (checked
 *   live by mac_prime-ryn, 10/23). `kind` is carried for a later headless seat.
 * Only a STICKY retire (`ryn_retire_seat`) takes a seat off: the sweep's
 * `auto-<N>d` retire is exactly the long-down seat this story exists to show.
 * Unlike part 1 there is no "abandoned after a day" cut: the roster says the
 * seat should run, so a week-old outage stays on the wall.
 *
 * Fail closed on missing data: a host that never published `desiredSeats`
 * (old CC, no roster file, unreadable file) says nothing, and an empty `seats`
 * list means the host wants no seats. The roster is the host's LAST report, so
 * a host that went dark still lists its seats as down.
 *
 * The bridge keeps ONE latest row per host id, and more than one publisher
 * writes it: RynTerminalAgent overwrites the Python CC's row with a sample
 * that has no `desiredSeats` (mac_prime-ryn, 10/23: the field shows on about
 * one read in three), and the Electron CC reports the same machine under a
 * differently cased id. So the roster is taken from ANY row for the host,
 * matched by the normalized machine id, and the last one seen is KEPT across
 * reads: a sample without the field never reads as "not reported".
 */
import type { HttpClient } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { parseUtcMs } from '../models/flock-telemetry.utils';
import type { SeatWorkQueue } from '../models/work-queue.model';
import type { Alert, AttentionInput } from './attention.logic';
import {
  SEAT_DOWN_SILENT_MS,
  deadSeatAlerts,
  seatDownAlert,
} from './dead-seats.logic';
import { normalizeMachineId } from './host-metrics.logic';
import { formatDuration } from './wallboard.logic';

/** One seat a host's roster wants running (the CC's `desiredSeats.seats`). */
export interface DesiredSeat {
  /** The bridge instance id (the roster's bridgeId). */
  instanceId: string;
  launchId: string | null;
  /** claude | codex | grok; absent reads as claude. */
  kind: string | null;
}

/** One host's published roster. */
export interface DesiredRosterHost {
  /** Normalized machine id (lowercase, no `.local`). */
  hostId: string;
  /** Hostname when the sample carries one, else the host id. */
  host: string;
  /** True when no row for the host is inside the bridge's stale window. */
  hostStale: boolean;
  /** The roster file's own `updated` date, as the CC sent it. */
  rosterUpdated: string | null;
  seats: DesiredSeat[];
}

/** The bridge row facts the rule reads (a work-queue row, retired included). */
export type RosterSeatRow = Pick<
  SeatWorkQueue,
  'instanceId' | 'lastSeen' | 'retired' | 'retiredReason'
>;

/**
 * `GET /api/host-metrics/latest`. The view records serialize PascalCase today
 * (`Hosts[].HostId`, `Metric`, live read 10/23), though the bridge comment says
 * camelCase, so both are read. `Metric` is the CC's own body, camelCase.
 */
export type HostMetricsLatestResponse = Record<string, unknown>;

/**
 * The rosters after one read of the latest metrics. A host whose rows in this
 * read carry `desiredSeats` takes the newest of them; a host whose rows do not
 * keeps the roster from `prev`. Staleness is re-derived each read: the host is
 * reporting while ANY of its rows is fresh.
 */
export function mergeDesiredRoster(
  prev: readonly DesiredRosterHost[] | null,
  resp: HostMetricsLatestResponse | null | undefined
): DesiredRosterHost[] {
  const reporting = new Map<string, boolean>();
  const fresh = new Map<string, { roster: DesiredRosterHost; atMs: number }>();
  const rows = field(asRecord(resp), 'hosts');
  for (const raw of Array.isArray(rows) ? rows : []) {
    const h = asRecord(raw);
    const key = normalizeMachineId(text(field(h, 'hostId')));
    if (!h || !key) continue;
    const stale = field(h, 'stale') === true;
    reporting.set(key, (reporting.get(key) ?? false) || !stale);
    const roster = rosterOf(h, key, stale);
    if (!roster) continue;
    const atMs = parseUtcMs(text(field(h, 'reportedAt'))) ?? 0;
    const cur = fresh.get(key);
    if (!cur || atMs >= cur.atMs) fresh.set(key, { roster, atMs });
  }
  const byHost = new Map<string, DesiredRosterHost>();
  for (const p of prev ?? []) byHost.set(p.hostId, p);
  for (const [key, f] of fresh) byHost.set(key, f.roster);
  return [...byHost.values()]
    .map((h) => ({ ...h, hostStale: !(reporting.get(h.hostId) ?? false) }))
    .sort((a, b) => a.hostId.localeCompare(b.hostId));
}

function rosterOf(
  h: Record<string, unknown>,
  key: string,
  stale: boolean
): DesiredRosterHost | null {
  const desired = asRecord(asRecord(field(h, 'metric'))?.['desiredSeats']);
  const rawSeats = desired?.['seats'];
  if (!Array.isArray(rawSeats)) return null;
  const seats: DesiredSeat[] = [];
  for (const s of rawSeats) {
    const r = asRecord(s);
    const id = text(r?.['instanceId']);
    if (!id) continue;
    seats.push({
      instanceId: id,
      launchId: text(r?.['launchId']),
      kind: text(r?.['kind']),
    });
  }
  return {
    hostId: key,
    host: text(field(h, 'hostname')) ?? text(field(h, 'hostId')) ?? key,
    hostStale: stale,
    rosterUpdated: text(desired?.['rosterUpdated']),
    seats,
  };
}

/** One read of the bridge's latest metrics per host (raw; see mergeDesiredRoster). */
export function loadLatestHostMetrics(
  http: HttpClient,
  base: string
): Observable<HostMetricsLatestResponse> {
  return http.get<HostMetricsLatestResponse>(`${base}/host-metrics/latest`);
}

/** An explicit retire. The sweep writes `auto-<N>d`; anything else is sticky. */
export function isStickyRetired(row: RosterSeatRow): boolean {
  return row.retired === true && !/^auto-\d+d$/.test(row.retiredReason ?? '');
}

/**
 * Crit "Seats down" rows for desired seats that are not running. `observedIds`
 * are seats a fresh snapshot or the CC sees; `alreadyDown` are the ids part 1
 * already put on the wall, so a seat is listed once.
 */
export function desiredSeatDownAlerts(
  hosts: readonly DesiredRosterHost[],
  rows: readonly RosterSeatRow[],
  observedIds: ReadonlySet<string>,
  alreadyDown: ReadonlySet<string>,
  nowMs: number
): Alert[] {
  const byId = new Map(rows.map((r) => [r.instanceId, r]));
  const out: Alert[] = [];
  const listed = new Set<string>();
  for (const h of hosts) {
    for (const s of h.seats) {
      const id = s.instanceId;
      const alertId = `seat|${id}|down`;
      if (listed.has(id) || alreadyDown.has(alertId) || observedIds.has(id))
        continue;
      const row = byId.get(id);
      if (row && isStickyRetired(row)) continue;
      const seenMs = parseUtcMs(row?.lastSeen ?? null);
      if (
        row &&
        !row.retired &&
        seenMs !== null &&
        nowMs - seenMs <= SEAT_DOWN_SILENT_MS
      )
        continue;
      listed.add(id);
      const host = `${h.host}${h.hostStale ? ' (host not reporting)' : ''}`;
      out.push(
        seatDownAlert(
          id,
          `${downReason(row, seenMs, nowMs)} · wanted on ${host}`
        )
      );
    }
  }
  return out;
}

/**
 * Every Seats down row: part 1's stopped seats (Bug 34685), then the desired
 * roster's seats that part 1 did not already list. A seat in the context/CC
 * seat signals is running, so neither rule lists it.
 */
export function seatDownAlerts(input: AttentionInput, nowMs: number): Alert[] {
  const observed = new Set(input.seats.map((s) => s.instanceId));
  const stopped = deadSeatAlerts(input.deadSeats ?? [], observed, nowMs);
  return [
    ...stopped,
    ...desiredSeatDownAlerts(
      input.desiredRoster ?? [],
      [...(input.deadSeats ?? []), ...(input.retiredSeats ?? [])],
      observed,
      new Set(stopped.map((a) => a.id)),
      nowMs
    ),
  ];
}

function downReason(
  row: RosterSeatRow | undefined,
  seenMs: number | null,
  nowMs: number
): string {
  if (!row) return 'never registered with the bridge';
  const contact =
    seenMs === null
      ? 'no bridge contact on record'
      : `no bridge contact for ${formatDuration(nowMs - seenMs)}`;
  return row.retired ? `${contact} · auto-retired` : contact;
}

/** A wire field by its camelCase name, or the PascalCase one the bridge sends. */
function field(o: Record<string, unknown> | null, name: string): unknown {
  if (!o) return undefined;
  return o[name] ?? o[name[0].toUpperCase() + name.slice(1)];
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}
